import ReactQuill from "react-quill";
import { ErrorBoundary } from "react-error-boundary";
import "@verity/components/core/fonts.css";
import "react-quill/dist/quill.snow.css";
import "./InstructionEditor.css";
import { Box } from "@mui/material";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { DeltaStatic, Sources, RangeStatic } from "quill";
import { InstructionEditorTooltipProvider } from "./InstructionEditorContext";
import { FormatValueMap, Format } from "./InstructionEditorTypes";
import { CustomToolBar } from "./InstructionEditorToolbar";

const modules = (toolbarId: string) => ({
	toolbar: {
		container: toolbarId,
	},
	keyboard: {
		bindings: {
			tab: true,
		},
	},
});

export default function InstructionEditor({
	defaultValue,
	deltaStaticDefaultValue,
	onChange,
}: {
	defaultValue?: string;
	deltaStaticDefaultValue?: DeltaStatic;
	onChange?: (value: string, delta: DeltaStatic, source: Sources, editor: ReactQuill.UnprivilegedEditor) => void;
}) {
	const [value, setValue] = useState(defaultValue || "");
	const [formats, setFormats] = useState<Partial<FormatValueMap>>({});
	const quillRef = useRef<ReactQuill>(null);

	// Since setup modal is in an iframe now, ran into issues with post messages not finding the LiveSessionStep
	const parentWindowRef = useRef<WindowProxy | null>(null);

	useEffect(() => {
		const targetWindow = window.parent !== window.self ? window.parent : window.top;
		targetWindow?.postMessage({ action: "editorReady" }, "*");

		const handleMessage = (event: MessageEvent) => {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			const action = event.data?.action;
			const requester = event.source as Window;
			if (action === "loadProctorGuide") {
				setValue(event.data.proctorGuide || "");
				parentWindowRef.current = requester;
				requester?.postMessage({ action: "proctorGuideLoaded" }, "*");
			}

			if (action === "checkEditorReady") {
				requester?.postMessage({ action: "editorReady" }, "*");
			}
		};

		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, []);

	// if in an iframe
	const handleChange = (content: string) => {
		setValue(content);
		parentWindowRef.current?.postMessage({ action: "updateProctorGuide", proctorGuide: content }, "*");
	};

	useEffect(() => {
		const editorContainer = document.querySelector(".ql-editor");
		if (!editorContainer) return;

		// sometimes browser's default behaviour is to deny the drop. Have to prevent browser's default for the drop event to fire.
		// https://stackoverflow.com/questions/8414154/html5-drop-event-doesnt-work-unless-dragover-is-handled
		editorContainer.addEventListener("dragover", (event) => {
			event.preventDefault();
		});

		editorContainer.addEventListener("drop", (event) => {
			event.preventDefault();
			const quill = quillRef.current?.getEditor();
			if (!quill) return;

			const files = (event as DragEvent).dataTransfer?.files;

			if (files?.length) {
				Array.from(files).forEach((file) => {
					if (file.type.startsWith("image/")) {
						const reader = new FileReader();

						reader.onload = () => {
							const range = quill.getSelection(true);
							quill.insertEmbed(range.index, "image", reader.result);
						};

						reader.readAsDataURL(file);
					} else {
						console.error(`Invalid file type: ${file.type}`);
					}
				});
			}
		});
	}, []);

	useEffect(() => {
		const quill = quillRef.current?.getEditor();
		if (!quill) return;

		const handleSelectionChange = (range: RangeStatic) => {
			if (range) {
				const currentFormats = quill.getFormat(range);
				setFormats(currentFormats);
			}
		};

		quill.on("selection-change", handleSelectionChange);

		return () => {
			quill.off("selection-change", handleSelectionChange);
		};
	}, []);

	// This makes the "send" button when embedding a video, focusable, because react quill doesn't do that
	useEffect(() => {
		const observer = new MutationObserver(() => {
			const el = document.querySelector(".ql-tooltip .ql-action");
			if (el) {
				const htmlEl = el as HTMLElement;

				// Make element keyboard focusable and role=button
				if (!htmlEl.hasAttribute("tabindex")) {
					htmlEl.setAttribute("tabindex", "0");
					htmlEl.setAttribute("role", "button");
					htmlEl.classList.add("quill-button");
				}

				// Replace ::after content with actual text
				if (htmlEl.textContent?.trim() === "") {
					htmlEl.textContent = "Send";
				}

				// Add keyboard event listener once
				if (!htmlEl.dataset.keyboardHandlerAttached) {
					const keyHandler = (e: KeyboardEvent) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							htmlEl.click();
						}
					};
					htmlEl.addEventListener("keydown", keyHandler);
					htmlEl.dataset.keyboardHandlerAttached = "true";

					const cleanupObserver = new MutationObserver(() => {
						if (!document.body.contains(htmlEl)) {
							htmlEl.removeEventListener("keydown", keyHandler);
							cleanupObserver.disconnect();
						}
					});
					cleanupObserver.observe(document.body, { childList: true, subtree: true });
				}
			}
		});

		observer.observe(document.body, { childList: true, subtree: true });

		return () => observer.disconnect();
	}, []);

	function handleFormatChange<K extends Format>(format: K, value: FormatValueMap[K]) {
		const quill = quillRef.current?.getEditor();
		if (!quill) return;

		const range = quill.getSelection();

		if (range && range.length === 0) {
			// No text selected, apply format for future typing at cursor
			quill.format(format, value);
		} else if (range && range.length > 0) {
			// Text selected, format it directly
			quill.format(format, value);
		} else {
			// No cursor (e.g. editor not focused), still set format for future input
			quill.format(format, value);
		}

		// Update React state tracking current formats (optional)
		setFormats((prev) => ({ ...prev, [format]: value }));

		setTimeout(() => {
			quill.focus(); // Focus cursor back in the text area
		}, 100);
	}

	// Replace useId() colon with underscore since react quill's container module expects `#{toolbarId}` which is css
	const toolbarId = useId().replace(/:/g, "_");
	const selector = `#${toolbarId}`;

	// Need to define the formats we are using for ReactQuill, since it now uses a custom component. If you don't pass in this array, then some formats don't work
	// Unfortunately, I couldn't figure out a way to define the formats once, since FormatValueMap is a type,
	// so in the future gotta add new tool to formatArray and FormatValueMap
	const formatArray = useMemo(
		() => [
			"size",
			"header",
			"color",
			"background",
			"font",
			"align",
			"bold",
			"italic",
			"underline",
			"strike",
			"blockquote",
			"code-block",
			"link",
			"image",
			"video",
			"list",
			"script",
			"indent",
			"direction",
			"clean",
		],
		[],
	);

	const toolbarModule = useMemo(() => modules(selector), [selector]);

	return (
		<InstructionEditorTooltipProvider>
			<Box
				data-text-editor="instruction"
				className="text-editor"
				sx={{
					border: "1px solid #6F6F6F",
					borderRadius: "4px",
					backgroundColor: "white",
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					width: "100%",
					boxSizing: "border-box",
					"& .ql-toolbar": {
						border: "none",
						borderBottom: "1px solid #E0E0E0",
					},
					"& .ql-container": {
						border: "none",
						"& .ql-editor": {
							height: "300px",
						},
					},
				}}
			>
				<ErrorBoundary
					fallbackRender={() => (
						<>
							<CustomToolBar onFormatChange={handleFormatChange} formats={formats} toolbarId={toolbarId} />
							<ReactQuill
								theme="snow"
								value={deltaStaticDefaultValue ?? value}
								onChange={onChange ?? handleChange}
								modules={toolbarModule}
								style={{ height: "230px" }}
								bounds={`[data-text-editor="instruction"]`}
								formats={formatArray}
							/>
						</>
					)}
				>
					<CustomToolBar onFormatChange={handleFormatChange} formats={formats} toolbarId={toolbarId} />
					<ReactQuill
						ref={quillRef}
						theme="snow"
						value={deltaStaticDefaultValue ?? value}
						onChange={onChange ?? handleChange}
						modules={toolbarModule}
						style={{
							wordWrap: "break-word",
							whiteSpace: "pre-wrap",
							overflowWrap: "break-word",
							wordBreak: "break-word",
						}}
						bounds={`[data-text-editor="instruction"]`}
						formats={formatArray}
					/>
				</ErrorBoundary>
			</Box>
		</InstructionEditorTooltipProvider>
	);
}
